// ════════════════════════════════════════════════════════════════════
// W'ILY'S PIER — pierShell3D.jsx   ·   THE BLENDER BOARDWALK
// ────────────────────────────────────────────────────────────────────
// caP'n, 2026-09-24: "let's use blender on the Unstable Pier, the actual
// pier/boardwalk … the plaza should be built like that already from
// Terror so use that as reference."
//
// `public/models/pier_shell.glb` is the pier's FIXED architecture only:
// real plank boards over a dark slab, the fascia, railings + posts, piles,
// X-braces + wales, the alley fences. Built by
//   gamemaster/models/sculpt/builders/pier_shell_blender/
// FROM pierPlan.js (verify/dumpPlan.mjs → pier_plan.json → Blender).
// Buildings, lamps, landmarks, props, bulbs and the sea rings stay live.
//
// ⚑ THE FAIL-SAFE LAW (the Plaza's): the code-built <Deck/> stands in while
// the glb loads, if it fails, and whenever the glb's PLAN SIGNATURE is not
// the live plan's (pierShellSig.js). Move a shopfront without re-running the
// builder and the pier quietly shows the code deck — a rail can never stand
// across a door.
//
// ⚑ MATERIALS ARE REPLACED BY NAME (pier_plank, pier_rail …) with the same
// painted timber the code deck wears; the glb's vertex colour carries each
// board's tone, the tide line on the piles. Repeated parts arrive as GPU
// instances (one draw per kind). Meshopt-compressed: three's own decoder.
// ════════════════════════════════════════════════════════════════════
import { Component, Suspense, useEffect, useMemo } from "react";
import * as THREE from "three";
import { useLoader } from "@react-three/fiber";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { MeshoptDecoder } from "three/examples/jsm/libs/meshopt_decoder.module.js";
import { Deck, SEA_Y } from "./pierDeck";
import { timberTex, dotTex } from "./pierPaint";
import { planSig } from "./pierShellSig";

export const PIER_SHELL_URL = `${process.env.PUBLIC_URL || ""}/models/pier_shell.glb`;
const LIVE_SIG = planSig();

// the code deck's own palette (pierDeck.jsx mats), so the two renderers match
const LOOK = {
  pier_plank: { color: "#e2d2b8", roughness: 0.88 },
  pier_slab: { color: "#3c2c1e", roughness: 1 },
  pier_fascia: { color: "#6e5438", roughness: 1 },
  pier_brace: { color: "#4a3a2a", roughness: 1 },
  pier_pile: { color: "#54402c", roughness: 1 },
  // ⚠ vertical timber takes half the key light the deck does and silhouettes
  // into the water: the code deck's small self-light, kept
  pier_fence: { color: "#8a6c4c", roughness: 0.95, emissive: "#2a1c10", emissiveIntensity: 0.3 },
  pier_rail: { color: "#a8865e", roughness: 0.85, emissive: "#4a3420", emissiveIntensity: 0.35 },
  pier_post: { color: "#96744e", roughness: 0.85, emissive: "#4a3420", emissiveIntensity: 0.3 },
  pier_iron: { color: "#3a3430", roughness: 0.45, metalness: 0.55, noMap: true },
};

function readMeta(scene) {
  let meta = null;
  scene.traverse((o) => {
    if (meta || !o.userData || !o.userData.ie_pier) return;
    try {
      meta = {
        ...JSON.parse(o.userData.ie_pier),
        bulbs: JSON.parse(o.userData.ie_bulbs || "[]"),
        piles: JSON.parse(o.userData.ie_piles || "[]"),
      };
    } catch (e) { meta = null; }
  });
  return meta;
}

function prepare(scene) {
  const root = scene.clone(true);
  const grain = timberTex();
  const made = {};
  // ⚠ vertexColors ONLY where the mesh has a colour attribute: without one,
  // WebGL reads the missing attribute as 0 and the whole pier draws BLACK
  // (a Blender export with no ACTIVE colour layer does exactly that)
  const matFor = (name, vc) => {
    const key = name + (vc ? "|vc" : "");
    if (made[key]) return made[key];
    const L = LOOK[name] || LOOK.pier_slab;
    const { noMap, ...look } = L;
    made[key] = new THREE.MeshStandardMaterial({ ...look, map: noMap ? null : grain, vertexColors: vc, metalness: look.metalness || 0 });
    return made[key];
  };
  root.traverse((o) => {
    if (!o.isMesh) return;
    const name = (o.material && o.material.name) || "";
    if (o.material) o.material.dispose();
    o.material = matFor(name.replace(/\.\d+$/, ""), !!o.geometry.attributes.color);
    o.frustumCulled = !o.isInstancedMesh;       // an instanced mesh's own bounds are one copy's — never cull it
  });
  return { root, made };
}

// the string of lights over every rail, and a ring where each pile meets the sea
function RailBulbs({ flat }) {
  const geo = useMemo(() => {
    const n = flat.length / 4;
    const pos = new Float32Array(n * 3), col = new Float32Array(n * 3);
    const tints = [new THREE.Color("#ffd98a"), new THREE.Color("#fff1c9"), new THREE.Color("#7ce7ff")];
    for (let i = 0; i < n; i++) {
      pos.set([flat[i * 4], flat[i * 4 + 1], flat[i * 4 + 2]], i * 3);   // the builder already sags them (RAIL_H + 0.29)
      const c = tints[flat[i * 4 + 3]] || tints[0];
      col.set([c.r, c.g, c.b], i * 3);
    }
    const g = new THREE.BufferGeometry();
    g.setAttribute("position", new THREE.BufferAttribute(pos, 3));
    g.setAttribute("color", new THREE.BufferAttribute(col, 3));
    return g;
  }, [flat]);
  const mat = useMemo(() => new THREE.PointsMaterial({
    size: 0.42, map: dotTex(), vertexColors: true, transparent: true, depthWrite: false,
    blending: THREE.AdditiveBlending, sizeAttenuation: true, toneMapped: false, fog: false,
  }), []);
  useEffect(() => () => { geo.dispose(); mat.dispose(); }, [geo, mat]);
  return <points geometry={geo} material={mat} frustumCulled={false} />;
}

function SeaRings({ flat }) {
  const mesh = useMemo(() => {
    const n = flat.length / 2;
    const geo = new THREE.RingGeometry(0.2, 0.36, 18);
    const mat = new THREE.MeshBasicMaterial({
      color: "#7CE7FF", transparent: true, opacity: 0.32, depthWrite: false,
      blending: THREE.AdditiveBlending, toneMapped: false, fog: false, side: THREE.DoubleSide,
    });
    const m = new THREE.InstancedMesh(geo, mat, n);
    const o = new THREE.Object3D();
    let s = 0x9174;
    const R = () => ((s = (s * 1664525 + 1013904223) >>> 0) / 4294967296);
    for (let i = 0; i < n; i++) {
      o.position.set(flat[i * 2], SEA_Y + 0.03, flat[i * 2 + 1]);
      o.rotation.set(-Math.PI / 2, 0, 0);
      o.scale.setScalar(0.9 + R() * 0.4);
      o.updateMatrix(); m.setMatrixAt(i, o.matrix);
    }
    m.instanceMatrix.needsUpdate = true; m.frustumCulled = false;
    return m;
  }, [flat]);
  useEffect(() => () => { mesh.geometry.dispose(); mesh.material.dispose(); }, [mesh]);
  return <primitive object={mesh} />;
}

function ShellModel({ world }) {
  const gltf = useLoader(GLTFLoader, PIER_SHELL_URL, (loader) => loader.setMeshoptDecoder(MeshoptDecoder));
  const meta = useMemo(() => readMeta(gltf.scene), [gltf]);
  const fits = !!meta && meta.sig === LIVE_SIG;
  const built = useMemo(() => (fits ? prepare(gltf.scene) : null), [gltf, fits]);
  useEffect(() => () => { if (built) Object.values(built.made).forEach((m) => m.dispose()); }, [built]);
  if (!fits) {
    console.warn("[pier] pier_shell.glb was built for plan", meta && meta.sig, "but the live plan is", LIVE_SIG,
      "— the code <Deck/> stands in. Rebuild: node verify/dumpPlan.mjs, then the Blender builder.");
    return <Deck world={world} />;
  }
  return (
    <group>
      <primitive object={built.root} />
      <RailBulbs flat={meta.bulbs} />
      <SeaRings flat={meta.piles} />
    </group>
  );
}

class GlbBoundary extends Component {
  constructor(p) { super(p); this.state = { bad: false }; }
  static getDerivedStateFromError() { return { bad: true }; }
  componentDidCatch(e) { console.warn("[pier] pier_shell.glb did not load — the code <Deck/> stands in", e); }
  render() { return this.state.bad ? this.props.fallback : this.props.children; }
}

// ⚑ `?deck=code` forces the code deck (a side-by-side check, and a way out)
const FORCE_CODE = typeof window !== "undefined" && /[?&]deck=code\b/.test(window.location.search);

export function PierShell({ world }) {
  const fb = <Deck world={world} />;
  if (FORCE_CODE) return fb;
  return (
    <GlbBoundary fallback={fb}>
      <Suspense fallback={fb}>
        <ShellModel world={world} />
      </Suspense>
    </GlbBoundary>
  );
}
export default PierShell;
